
import { TrendingUp } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { format } from "date-fns";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface NetworkAprProps {
  apr: number;
  rewardsPerMinute: number;
  isLoading: boolean;
  tooltipContent: React.ReactNode;
}

export function NetworkApr({ apr, rewardsPerMinute, isLoading, tooltipContent }: NetworkAprProps) {
  const { data: historicalData, isLoading: isHistoricalLoading, error } = useQuery({
    queryKey: ['historicalApr'],
    queryFn: async () => {
      console.log("Fetching historical APR data");
      
      // Get ALL available daily data without limits
      const { data: dailyData, error: dailyError } = await supabase
        .from('daily_aggregated_stats')
        .select('date, latest_apr_percentage')
        .order('date', { ascending: true });

      if (dailyError) {
        console.error("Error fetching daily APR data:", dailyError);
        throw dailyError;
      }
      
      console.log("Daily APR data fetched:", dailyData?.length || 0, "records");
      console.log("Raw APR data sample:", dailyData?.slice(-5));

      // Get the last two daily values for difference calculation
      const { data: lastTwo } = await supabase
        .from('daily_aggregated_stats')
        .select('latest_apr_percentage')
        .order('date', { ascending: false })
        .limit(2);

      if (!dailyData || dailyData.length === 0) {
        console.log("No daily APR data available");
        return {
          historicalData: [],
          lastTwoValues: []
        };
      }

      // Process data for chart - FIXED: Use latest_apr_percentage correctly
      const chartData = dailyData.map(item => {
        const date = new Date(item.date);
        const aprValue = Number(item.latest_apr_percentage || 0);
        console.log(`Processing date ${item.date}: APR = ${aprValue}`);
        return {
          rawDate: item.date,
          date: date,
          displayDate: format(date, 'MMM dd'),
          value: aprValue  // This should be the APR percentage
        };
      });
      
      console.log("Processed APR chart data:", chartData.length, "records");
      console.log("Final APR chart values sample:", chartData.slice(-5).map(d => ({ date: d.displayDate, value: d.value })));
      console.log("Chart data being passed to LineChart:", chartData.slice(0, 3), "...", chartData.slice(-3));
      
      return {
        historicalData: chartData,
        lastTwoValues: lastTwo || []
      };
    }
  });

  if (error) {
    console.error("Error in historical APR data query:", error);
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Network APR</CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  const difference = historicalData?.lastTwoValues.length === 2
    ? (historicalData.lastTwoValues[0]?.latest_apr_percentage || 0) - (historicalData.lastTwoValues[1]?.latest_apr_percentage || 0)
    : 0;

  return (
    <UITooltip>
      <TooltipTrigger asChild>
        <Card className="w-full">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Network APR</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex flex-col space-y-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold">{apr.toFixed(2)}%</span>
                  {difference !== 0 && (
                    <span className={`text-sm ${difference > 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {difference > 0 ? '+' : ''}{difference.toFixed(2)}%
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  <UITooltip>
                    <TooltipTrigger className="cursor-help">
                      <span>Based on daily network performance</span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>APR is calculated from actual rewards earned across the network in the last 24 hours</p>
                    </TooltipContent>
                  </UITooltip>
                </div>
              </div>
              
              <div className="h-[120px] mt-4">
                {!isHistoricalLoading && historicalData?.historicalData && historicalData.historicalData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart 
                      data={historicalData.historicalData} 
                      margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                    >
                      <XAxis
                        dataKey="displayDate"
                        stroke="#888888"
                        fontSize={10}
                        tickLine={false}
                        axisLine={false}
                        interval="preserveStartEnd"
                        tickMargin={5}
                      />
                      <YAxis
                        stroke="#888888"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => `${value.toFixed(1)}%`}
                        width={45}
                        tickCount={5}
                        domain={['auto', 'auto']}
                      />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div className="rounded-lg border bg-background p-2 shadow-sm">
                                <div className="grid grid-cols-2 gap-2">
                                  <div className="flex flex-col">
                                    <span className="text-[0.70rem] uppercase text-muted-foreground">
                                      Date
                                    </span>
                                    <span className="font-bold text-sm">
                                      {data.displayDate}
                                    </span>
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="text-[0.70rem] uppercase text-muted-foreground">
                                      Network APR
                                    </span>
                                    <span className="font-bold text-sm">
                                      {`${Number(payload[0].value).toFixed(2)}%`}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#6e46ae"
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    {isHistoricalLoading ? "Loading data..." : error ? "Error loading data" : "No historical data available"}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </TooltipTrigger>
      <TooltipContent>
        {tooltipContent}
      </TooltipContent>
    </UITooltip>
  );
}
